<script>
import { ref, computed } from 'vue';
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import AddLabel from 'shared/components/ui/dropdown/AddLabel.vue';
import { specialLangs } from 'shared/constants/specialLangs';

export default {
  components: {
    Spinner,
    LabelDropdown,
    AddLabel,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    useAILabels: {
      type: Boolean,
      default: false,
    },
    emptyStateText: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { isAdmin } = useAdmin();

    const {
      savedLabels,
      activeLabels,
      aiLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
    } = useConversationLabels();

    const showSearchDropdownLabel = ref(false);

    const toggleLabels = () => {
      showSearchDropdownLabel.value = !showSearchDropdownLabel.value;
    };

    const closeDropdownLabel = () => {
      showSearchDropdownLabel.value = false;
    };

    const keyboardEvents = {
      KeyL: {
        action: e => {
          e.preventDefault();
          toggleLabels();
        },
      },
      Escape: {
        action: () => {
          if (showSearchDropdownLabel.value) {
            toggleLabels();
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);
    return {
      isAdmin,
      savedLabels,
      activeLabels: computed(() => {
        const labels = props.useAILabels ? aiLabels.value : activeLabels.value;
        return labels.filter(label => {
          const title = label?.title?.toLowerCase();
          const showTitle = label?.show_title?.toLowerCase();
          
          return title && showTitle &&
                 !specialLangs.includes(title) && 
                 !specialLangs.includes(showTitle);
        });
      }),
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
      showSearchDropdownLabel,
      closeDropdownLabel,
      toggleLabels,
      readonly: props.readonly,
      useAILabels: props.useAILabels,
      emptyStateText: props.emptyStateText,
    };
  },
  data() {
    return {
      selectedLabels: [],
    };
  },

  computed: {
    ...mapGetters({
      conversationUiFlags: 'conversationLabels/getUIFlags',
    }),
  },
};
</script>

<template>
  <div class="sidebar-labels-wrap">
    <div
      v-if="!conversationUiFlags.isFetching"
      class="contact-conversation--list"
    >
      <div
        v-on-clickaway="closeDropdownLabel"
        class="label-wrap flex flex-wrap"
        @keyup.esc="closeDropdownLabel"
      >
        <AddLabel v-if="!readonly" @add="toggleLabels" />
        <woot-label
          v-for="label in activeLabels"
          :key="label.id"
          :title="label.show_title"
          :description="label.description"
          :show-close="!readonly"
          :color="label.color"
          variant="smooth"
          :show-tooltip="true"
          class="max-w-[calc(100%-0.5rem)]"
          @remove="removeLabelFromConversation"
        />

        <!-- 空状态展示 -->
        <div
          v-if="readonly && activeLabels.length === 0 && emptyStateText"
          class="empty-state-text"
        >
          {{ emptyStateText }}
        </div>

        <div v-if="!readonly" class="dropdown-wrap">
          <div
            :class="{ 'dropdown-pane--open': showSearchDropdownLabel }"
            class="dropdown-pane"
          >
            <LabelDropdown
              v-if="showSearchDropdownLabel"
              :account-labels="accountLabels"
              :selected-labels="savedLabels"
              :allow-creation="isAdmin"
              @add="addLabelToConversation"
              @remove="removeLabelFromConversation"
            />
          </div>
        </div>
      </div>
    </div>
    <Spinner v-else />
  </div>
</template>

<style lang="scss" scoped>
.sidebar-labels-wrap {
  margin-bottom: 0;
}
.contact-conversation--list {
  width: 100%;

  .label-wrap {
    line-height: var(--space-medium);
    position: relative;

    .dropdown-wrap {
      display: flex;
      left: -1px;
      margin-right: var(--space-medium);
      position: absolute;
      top: var(--space-medium);
      width: 100%;

      .dropdown-pane {
        width: 100%;
        box-sizing: border-box;
      }
    }
  }
}

.error {
  color: var(--r-500);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
}

.empty-state-text {
  color: #62666E;
  background-color: #F5F5F5;
  padding: 4px 6px;
  display: inline-block;
  font-size: 0.7rem;
  line-height: 1;
}
</style>
